import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useI18n, SUPPORTED_LOCALES, type Locale } from "@/lib/i18n";

const PREFIX_LOCALES = SUPPORTED_LOCALES.filter((l) => l !== "tr");
const PREFIX_RE = new RegExp(`^/(${PREFIX_LOCALES.join("|")})(/|$)`);

/** Pure: prepend `/<locale>` to a canonical path unless locale === 'tr' (root). */
export function localePath(path: string, locale: Locale): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (locale === "tr") return p;
  if (p === "/") return `/${locale}`;
  return `/${locale}${p}`;
}

/** Strip any leading `/<locale>` prefix → canonical path (TR form). */
export function stripLocale(pathname: string): string {
  const stripped = pathname.replace(PREFIX_RE, "/");
  return stripped || "/";
}

/** Hook: returns a function that prefixes the current locale. Use for every `<Link to={lp("/x")}>`. */
export function useLp() {
  const { locale } = useI18n();
  return (path: string) => localePath(path, locale);
}

/** Hook: switch locale AND navigate to the equivalent localized URL of the current page. */
export function useChangeLocale() {
  const { setLocale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  return (next: Locale) => {
    setLocale(next);
    const canonical = stripLocale(location.pathname);
    const target = localePath(canonical, next);
    navigate(target + location.search + location.hash);
  };
}

/** Hook: read `:locale` param if present; returns null when on root (TR) routes. */
export function useUrlLocale(): Locale | null {
  const params = useParams<{ locale?: string }>();
  const l = params.locale as Locale | undefined;
  if (!l) return null;
  return SUPPORTED_LOCALES.includes(l) ? l : null;
}
